import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Typography
} from '@mui/material';
import {
  FinishDetailPairEntry,
  useFinishGroup,
  useGroupFinishState
} from '../../api/useGroupFinish';
import { formatUsd } from '../../utils/format';

export const FinishGroupButton = ({
  groupId,
  groupName
}: {
  groupId: string;
  groupName: string;
}) => {
  const { data: state } = useGroupFinishState(groupId);
  const finishMutation = useFinishGroup(groupId);

  const hasRun = state?.pnlSummary != null;

  const [open, setOpen] = useState(false);
  const [excludedIds, setExcludedIds] = useState<Set<string>>(new Set());

  const breakdown: FinishDetailPairEntry[] = [
    ...(state?.computed ?? []),
    ...(state?.pending ?? []),
    ...(state?.excluded ?? [])
  ];

  useEffect(() => {
    if (!open) return;
    const auto = new Set(state?.autoSuggestExcludePairIds ?? []);
    state?.excluded?.forEach(p => auto.add(p.pairId));
    setExcludedIds(auto);
  }, [open, state]);

  const toggle = (pairId: string) => {
    setExcludedIds(prev => {
      const next = new Set(prev);
      if (next.has(pairId)) next.delete(pairId);
      else next.add(pairId);
      return next;
    });
  };

  const handleSubmit = () => {
    finishMutation.mutate({
      excludePairIds: Array.from(excludedIds)
    });
    setOpen(false);
  };

  return (
    <>
      <Stack direction="row" spacing={0.5} alignItems="center">
        {hasRun && state?.pnlSummary && (
          <Chip
            label={`${state.pnlSummary.fullyFinished ? 'Finished' : 'Partial'} ${
              state.pnlSummary.computedCount
            }/${state.pnlSummary.computedCount + state.pnlSummary.pendingCount} · Net ${formatUsd(
              state.pnlSummary.groupNetUsd
            )}`}
            size="small"
            color={state.pnlSummary.fullyFinished ? 'success' : 'default'}
            variant="outlined"
            sx={{ fontSize: '0.7rem' }}
          />
        )}
        <Button
          color="warning"
          variant="outlined"
          size="small"
          sx={{ fontSize: '0.7rem', whiteSpace: 'nowrap' }}
          onClick={() => setOpen(true)}
        >
          {hasRun ? 'Re-finish' : 'Finish'}
        </Button>
      </Stack>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontSize: '1rem' }}>
          Finish group — {groupName}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={1.5}>
            <Typography sx={{ fontSize: '0.85rem' }}>
              Incremental finish: computes the pairs that have resolved on Kalshi AND
              redeemed on Polymarket, adds them to the group total, and expires them.
              Pending pairs keep flowing. Safe to re-run anytime to complete the rest.
            </Typography>

            <Box>
              <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, mb: 0.5 }}>
                Exclude from finish (resolve later)
              </Typography>
              <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', mb: 0.5 }}>
                Long-tail outrights are pre-checked; confirm which pairs to take out of
                this finish so the group can complete.
              </Typography>
              {breakdown.length === 0 ? (
                <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                  No pairs to show.
                </Typography>
              ) : (
                <Box sx={{ maxHeight: 240, overflowY: 'auto', pl: 0.5 }}>
                  <Stack spacing={0}>
                    {breakdown.map(pair => (
                      <FormControlLabel
                        key={pair.pairId}
                        control={
                          <Checkbox
                            size="small"
                            checked={excludedIds.has(pair.pairId)}
                            onChange={() => toggle(pair.pairId)}
                          />
                        }
                        label={
                          <Typography sx={{ fontSize: '0.72rem' }} noWrap>
                            {pair.pairName}
                            {pair.ruleId ? ` · ${pair.ruleId}` : ''}
                          </Typography>
                        }
                      />
                    ))}
                  </Stack>
                </Box>
              )}
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button size="small" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            color="warning"
            disabled={finishMutation.isLoading}
            onClick={handleSubmit}
          >
            {finishMutation.isLoading
              ? 'Finishing group…'
              : hasRun
                ? 'Re-run finish'
                : 'Finish group'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
